import { layoutRect, layoutCoordinate } from "../utils/displayDensity";
import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import "./loginTest.css";
import AdminDashboardMenu from "../components/AdminDashboardMenu";
import { useAuth } from "../contexts/AuthContext";
import { LockKeyhole, LogOut, UserRound } from "lucide-react";
import { signOut } from "../auth/cognito";
import { canAccessRoute } from "../config/routeAccess";
import ResearchSearch from "../components/ResearchSearch";
import DashboardBubbleTexture from "../components/DashboardBubbleTexture";
import { BLADE_BUBBLE_MODE } from "../config/bladeAppearance";
import BladeSelectorEditorial, { EDITORIAL_TEXTURES, EditorialBladeContent } from "../components/BladeSelectorEditorial";
import { selectorThemeStyle } from "../config/selectorThemes";
import { useDashboardTransition } from "../components/DashboardTransition";
import { SELECTOR_DASHBOARDS } from "../config/selectorDashboards";

const asset = (name) => `${process.env.PUBLIC_URL}/${name}`;

export const EXPERIMENTAL_DASHBOARDS = SELECTOR_DASHBOARDS;
const DEFAULT_SELECTION = EXPERIMENTAL_DASHBOARDS.findIndex((dashboard) => dashboard.route === "/mindshare");

function DashboardSelectionPanel({ dashboard, index, editorial, selected, adjacent, launching, locked, onSelect, onHover = onSelect, onLaunch, buttonRef }) {
  const touchSelectedRef = useRef(false);
  const lockIndicator = locked && selected && (
    <span className="lt-panel-lock">
      <LockKeyhole size={30} strokeWidth={1.35} role="img" aria-label="Locked" />
      <span id={`lt-lock-help-${dashboard.route.slice(1)}`} className="lt-lock-tooltip" role="tooltip">Contact info@mindgamedata.com for details</span>
        </span>
  );
  return (
    <button
      ref={buttonRef}
      type="button"
      data-dashboard-route={dashboard.route}
      className={`lt-panel${dashboard.route === "/mindshare" ? " lt-panel-mindshare" : ""}${selected ? " is-selected" : ""}${adjacent ? " is-adjacent" : ""}${launching ? " is-launching" : ""}${locked ? " is-locked" : ""}`}
      style={{ "--lt-color": dashboard.color, "--lt-light": dashboard.light, "--lt-rgb": dashboard.rgb, "--lt-logo-size": dashboard.logoSize || 1, "--bs-index": index, ...selectorThemeStyle(dashboard.route) }}
      aria-label={`Open ${dashboard.name}`}
      aria-pressed={selected}
      aria-disabled={locked}
      aria-describedby={locked && selected ? `lt-lock-help-${dashboard.route.slice(1)}` : undefined}
      tabIndex={selected ? 0 : -1}
      onPointerEnter={(event) => { if (event.pointerType === "mouse") onHover(event); }}
      onPointerMove={(event) => { if (event.pointerType === "mouse") onHover(event); }}
      onPointerDown={(event) => {
        touchSelectedRef.current = event.pointerType !== "mouse" && !selected;
        if (touchSelectedRef.current) onSelect();
      }}
      onFocus={onSelect}
      onClick={() => {
        if (locked) return;
        if (touchSelectedRef.current) { touchSelectedRef.current = false; return; }
        onLaunch();
      }}
    >
      {editorial ? <>
        <EditorialBladeContent dashboard={dashboard} index={index} selected={selected} locked={locked} />
        {lockIndicator}
      </> : <>
        <span className="lt-panel-metal" aria-hidden="true" />
        <span className="lt-panel-brand" aria-hidden="true" />
        {BLADE_BUBBLE_MODE === "subtle" && <DashboardBubbleTexture seed={dashboard.name} active={selected && !launching} />}
        <span className="lt-panel-edge" aria-hidden="true" />
        <span className="lt-panel-content">
          <img className="lt-panel-icon" src={asset(dashboard.icon)} alt="" draggable="false" />
          <img className="lt-panel-logo" src={asset(dashboard.logo)} alt="" draggable="false" />
          {lockIndicator}
          </span>
      </>}
    </button>
  );
}

export default function LoginTest({ enterAnimation = false, fromLogin = false }) {
  const navigate = useNavigate();
  const location = useLocation();
  const editorial = location.pathname === "/logintest" || location.pathname === "/login";
  const atmosphereV2 = editorial;
  const beginTransition = useDashboardTransition();
  const returnIndex = SELECTOR_DASHBOARDS.findIndex(dashboard => dashboard.route === location.state?.returnBlade);
  const returningHome = returnIndex >= 0;
  const [intro, setIntro] = useState(editorial && !returningHome);
  const [initialExpansion, setInitialExpansion] = useState(editorial && !returningHome);
  const [pageVisible, setPageVisible] = useState(() => document.visibilityState === "visible");
  const [entering, setEntering] = useState(Boolean((enterAnimation || location.state?.fromLogin) && !editorial));
  const { currentUser, loadingUser } = useAuth();
  const [activeIndex, setActiveIndex] = useState(returningHome ? returnIndex : DEFAULT_SELECTION);
  const [launching, setLaunching] = useState(false);
  const [researchLaunching, setResearchLaunching] = useState(false);
  const [navigationError, setNavigationError] = useState("");
  const [launchLogoSize, setLaunchLogoSize] = useState(null);
  const timerRef = useRef(null);
  const launchingRef = useRef(false);
  const buttonsRef = useRef([]);
  const screenRef = useRef(null);
  const keyboardSelectionRef = useRef(false);
  const pointerPositionRef = useRef(null);
  const active = EXPERIMENTAL_DASHBOARDS[activeIndex];
  const handleLogout = () => {
    try {
      signOut();
      localStorage.removeItem("auth");
      localStorage.removeItem("mg_session_hint");
      localStorage.removeItem("mg_token");
      window.location.assign("/login?logout=1");
    } catch (error) {
      console.error("Unable to log out:", error);
      setNavigationError("Unable to log out. Please try again.");
    }
  };
  const canAccess = (route) => !loadingUser && canAccessRoute(currentUser, route);

  useEffect(() => () => clearTimeout(timerRef.current), []);
  useLayoutEffect(() => {
    if (!atmosphereV2) return undefined;
    const screen = screenRef.current;
    const panel = buttonsRef.current[activeIndex];
    if (!screen || !panel) return undefined;

    const updateLightPosition = () => {
      const bounds = panel.getBoundingClientRect();
      screen.style.setProperty("--bs-active-x", `${bounds.left + bounds.width / 2}px`);
      screen.style.setProperty("--bs-active-y", `${bounds.top + bounds.height / 2}px`);
    };
    const observer = typeof ResizeObserver === "function"
      ? new ResizeObserver(updateLightPosition)
      : null;
    updateLightPosition();
    observer?.observe(panel);
    panel.addEventListener("transitionend", updateLightPosition);
    window.addEventListener("resize", updateLightPosition);
    return () => {
      observer?.disconnect();
      panel.removeEventListener("transitionend", updateLightPosition);
      window.removeEventListener("resize", updateLightPosition);
    };
  }, [activeIndex, atmosphereV2]);
  useEffect(() => {
    if (!editorial || returningHome) return undefined;
    const reduced = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    const introDelay = reduced ? 0 : 500;
    const timer = setTimeout(() => setIntro(false), introDelay);
    const expansionTimer = setTimeout(() => setInitialExpansion(false), introDelay + (reduced ? 0 : 700));
    const updateVisibility = () => setPageVisible(document.visibilityState === "visible");
    document.addEventListener("visibilitychange", updateVisibility);
    return () => {
      clearTimeout(timer);
      clearTimeout(expansionTimer);
      document.removeEventListener("visibilitychange", updateVisibility);
    };
  }, [editorial, fromLogin, returningHome]);
  useEffect(() => { screenRef.current?.focus({ preventScroll: true }); }, []);
  useEffect(() => {
    if (!entering) return undefined;
    const timeout = setTimeout(() => setEntering(false), 800);
    return () => clearTimeout(timeout);
  }, [entering]);
  useEffect(() => {
    if (launching && !canAccess(active.route)) {
      clearTimeout(timerRef.current);
      launchingRef.current = false;
      setLaunching(false);
    }
  }, [currentUser, loadingUser, launching, active.route]);

  const select = (index) => {
    if (!launchingRef.current) {
      if (editorial) setIntro(false);
      setActiveIndex(index);
    }
  };
  const hover = (index, event) => {
    const previous = pointerPositionRef.current;
    const position = { x: layoutCoordinate(event.clientX), y: layoutCoordinate(event.clientY) };
    pointerPositionRef.current = position;
    if (keyboardSelectionRef.current) {
      // Expanding blades can enter a stationary pointer during keyboard wrapping.
      if (event.type !== "pointermove" || !previous ||
        (previous.x === position.x && previous.y === position.y)) return;
      keyboardSelectionRef.current = false;
    }
    select(index);
  };
  const launch = (index) => {
    if (launchingRef.current || !canAccess(EXPERIMENTAL_DASHBOARDS[index].route)) return;
    setNavigationError("");
    const logo = buttonsRef.current[index]?.querySelector(editorial ? ".bs-platform-mark img" : ".lt-panel-logo");
    if (logo) {
      const size = getComputedStyle(logo);
      setLaunchLogoSize({ width: size.width, height: size.height });
    }
    launchingRef.current = true;
    setEntering(false);
    setActiveIndex(index);
    setLaunching(true);
    const reduced = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (editorial) {
      let cancelTransition;
      cancelTransition = beginTransition(EXPERIMENTAL_DASHBOARDS[index].route, layoutRect(buttonsRef.current[index]), {
        onNavigate: () => {
          try {
            navigate(EXPERIMENTAL_DASHBOARDS[index].route, { state: { fromBlades: true } });
          } catch (error) {
            console.error("Dashboard navigation failed:", error);
            cancelTransition?.();
            launchingRef.current = false;
            setLaunching(false);
            setNavigationError("Unable to open this dashboard. Please try again.");
          }
        },
      });
      return;
    }
    timerRef.current = setTimeout(() => navigate(EXPERIMENTAL_DASHBOARDS[index].route, { state: { fromBlades: true } }), reduced ? 0 : 1080);
  };

  const handleKeyDown = (event) => {
    if (event.target.closest?.(".research-search, .mg-admin-dashboard-menu, .bs-account-controls")) return;
    if (launchingRef.current) return;
    let next = activeIndex;
    if (event.key === "ArrowRight" || event.key === "ArrowDown") next = (activeIndex + 1) % EXPERIMENTAL_DASHBOARDS.length;
    else if (event.key === "ArrowLeft" || event.key === "ArrowUp") next = (activeIndex + EXPERIMENTAL_DASHBOARDS.length - 1) % EXPERIMENTAL_DASHBOARDS.length;
    else if (event.key === "Home") next = 0;
    else if (event.key === "End") next = EXPERIMENTAL_DASHBOARDS.length - 1;
    else if (event.key === "Enter") { event.preventDefault(); launch(activeIndex); return; }
    else return;
    event.preventDefault();
    keyboardSelectionRef.current = true;
    select(next);
    buttonsRef.current[next]?.focus({ preventScroll: true });
  };

  return (
    <main ref={screenRef} tabIndex={-1} className={`lt-screen${editorial ? " bs-screen" : ""}${atmosphereV2 ? " bs-atmosphere-v2" : ""}${returningHome ? " bs-returning-home" : ""}${!pageVisible ? " bs-motion-paused" : ""}${fromLogin ? " bs-from-login" : ""}${editorial && intro ? " bs-intro" : ""}${initialExpansion ? " bs-initial-expansion" : ""}${entering ? " is-entering" : ""}${launching ? " is-launching" : ""}${researchLaunching ? " is-research-launching" : ""}`} onKeyDown={handleKeyDown} style={{ ...(editorial ? EDITORIAL_TEXTURES : {}), ...(atmosphereV2 ? selectorThemeStyle(active.route) : {}), "--lt-launch-rgb": active.route === "/mindshare" ? "0,0,0" : active.rgb, "--lt-launch-logo-width": launchLogoSize?.width, "--lt-launch-logo-height": launchLogoSize?.height }}>
      {editorial && <BladeSelectorEditorial paused={!pageVisible} />}
      {atmosphereV2 && <div className="bs-ambient-field" aria-hidden="true" />}
      {navigationError && <p className="bs-navigation-error" role="alert">{navigationError}</p>}
      {currentUser && editorial && <div className="lt-research-search"><ResearchSearch disabled={launching || researchLaunching} onSelect={(game) => {
        if (launchingRef.current) return;
        launchingRef.current = true;
        setResearchLaunching(true);
        timerRef.current = setTimeout(() => navigate(`/logintest/game/${encodeURIComponent(game.id)}`, { state: { game } }), window.matchMedia("(prefers-reduced-motion: reduce)").matches ? 0 : 320);
      }} /></div>}
      <div className="lt-selector" role="group" aria-label="Select a mindGAME dashboard" aria-busy={launching}>
        {EXPERIMENTAL_DASHBOARDS.map((dashboard, index) => (
          <DashboardSelectionPanel key={dashboard.route} dashboard={dashboard} index={index} editorial={editorial} selected={activeIndex === index} launching={launching && activeIndex === index} locked={!canAccess(dashboard.route)}
            adjacent={Math.abs(index - activeIndex) === 1}
            buttonRef={(element) => { buttonsRef.current[index] = element; }} onSelect={() => select(index)} onHover={(event) => hover(index, event)} onLaunch={() => launch(index)} />
        ))}
      </div>
      {!editorial && <div className="lt-selection-rail" aria-hidden="true">
        {EXPERIMENTAL_DASHBOARDS.map((dashboard, index) => <span key={dashboard.route} className={activeIndex === index ? "is-selected" : ""} style={{ "--lt-color": dashboard.color, "--lt-rail-fill": dashboard.route === "/tiktok" ? "linear-gradient(90deg, #25f4ee 0%, #8b9c9e 45%, #9c9096 55%, #fe2c55 100%)" : dashboard.route === "/mindshare" ? "linear-gradient(90deg, #3fa9f5 0%, #8b9ca8 45%, #a89e96 55%, #ff9b70 100%)" : dashboard.color }} />)}
      </div>}
      <div className="lt-launch-wash" aria-hidden="true" />
      {currentUser && editorial && !launching && !researchLaunching && (
        <nav className="bs-account-controls" aria-label="Your account">
          <button type="button" onClick={() => navigate("/profile")}><UserRound size={16} aria-hidden="true" /><span>Identity</span></button>
          <button type="button" onClick={handleLogout}><LogOut size={16} aria-hidden="true" /><span>Log out</span></button>
        </nav>
      )}
      {currentUser?.is_admin && !launching && <AdminDashboardMenu />}
    </main>
  );
}